import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Search, Clear } from "@mui/icons-material";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { format } from "date-fns";
import TokenDisplay from "../../components/TokenDisplay";

interface LLMRequest {
  id: string;
  prompt: string;
  answer: string;
  status: string;
  model: string;
  error?: string;
  promptTokens: number;
  outputTokens: number;
  estimatedCost: number;
  requestTime: number;
  requestTimestamp: string;
  completionTimestamp?: string;
}

export function RequestsList() {
  const [requests, setRequests] = useState<LLMRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [idSearchQuery, setIdSearchQuery] = useState<string>("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const workflowId = searchParams.get("workflowId");
  const recurringWorkflowId = searchParams.get("recurringWorkflowId");

  useEffect(() => {
    fetchRequests();
  }, [workflowId, recurringWorkflowId]);

  const fetchRequests = async (searchText?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Build request body with filters
      const requestBody: any = {
        limit: 100,
        offset: 0,
      };
      
      if (workflowId) {
        requestBody.temporalWorkflowId = workflowId;
      }
      if (recurringWorkflowId) {
        requestBody.recurringWorkflowId = recurringWorkflowId;
      }
      if (searchText?.trim()) {
        requestBody.searchQuery = searchText.trim();
      }
      
      // Fetch requests with filters
      const response = await apiClient.post<any>("/gpt/requests/", requestBody);
      // Ensure we have an array
      const requestsData = Array.isArray(response)
        ? response
        : response.requests
          ? response.requests
          : [];
      setRequests(requestsData);
    } catch (error) {
      console.error("Error fetching LLM requests:", error);
      setError("Failed to load LLM requests");
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (id: string) => {
    navigate(`/admin/request/${id}`);
  };

  const handleSearch = () => {
    fetchRequests(searchQuery);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    fetchRequests();
  };

  const handleIdSearch = () => {
    if (idSearchQuery.trim()) {
      navigate(`/admin/request/${idSearchQuery.trim()}`);
    }
  };

  const handleClearIdSearch = () => {
    setIdSearchQuery("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleIdKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleIdSearch();
    }
  };

  const truncateText = (text: string, maxLength: number = 100): string => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const getStatusColor = (status: string): "success" | "error" | "default" => {
    switch (status) {
      case "completed":
      case "success":
        return "success";
      case "failed":
      case "error":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <AdminPage>
        <Box
          sx={{ display: "flex", justifyContent: "center", padding: "40px" }}
        >
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <Box sx={{ padding: "20px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Typography variant="h4">
            LLM Requests
          </Typography>
          {(workflowId || recurringWorkflowId) && (
            <Chip 
              label={workflowId ? `Workflow: ${workflowId}` : `Recurring Workflow: ${recurringWorkflowId}`}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search by request ID..."
            value={idSearchQuery}
            onChange={(e) => setIdSearchQuery(e.target.value)}
            onKeyDown={handleIdKeyDown}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: idSearchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearIdSearch} size="small">
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }}
          />
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search in prompts and responses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} size="small">
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Timestamp</strong>
                </TableCell>
                <TableCell>
                  <strong>Model</strong>
                </TableCell>
                <TableCell>
                  <strong>Status</strong>
                </TableCell>
                <TableCell>
                  <strong>Prompt</strong>
                </TableCell>
                <TableCell>
                  <strong>Response</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Tokens</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Cost</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Time (s)</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No LLM requests found
                  </TableCell>
                </TableRow>
              ) : (
                requests.map((request) => (
                  <TableRow
                    key={request.id}
                    hover
                    onClick={() => handleRowClick(request.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      {request.requestTimestamp &&
                        format(
                          new Date(request.requestTimestamp),
                          "MMM dd HH:mm:ss"
                        )}
                    </TableCell>
                    <TableCell>
                      <Chip label={request.model} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.status}
                        size="small"
                        color={getStatusColor(request.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={request.prompt}>
                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                          {truncateText(request.prompt)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={request.answer || request.error || "No response"}
                      >
                        <Typography variant="body2" sx={{ maxWidth: 300 }}>
                          {truncateText(
                            request.answer || request.error || "No response"
                          )}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <TokenDisplay 
                        promptTokens={request.promptTokens}
                        outputTokens={request.outputTokens}
                        variant="slash"
                      />
                    </TableCell>
                    <TableCell align="right">
                      ${request.estimatedCost.toFixed(4)}
                    </TableCell>
                    <TableCell align="right">
                      {request.requestTime.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </AdminPage>
  );
}

export default RequestsList;
